import { X, ShieldAlert, BookOpen } from "lucide-react";
import { formatFullCurrency, getViolationTypeLabel } from "@/utils/helpers";
import InfoTooltip from "@/components/common/InfoTooltip";
import StatuteDetailsSection from "@/components/common/StatuteDetailsSection";

const DamagesDetailModal = ({ 
  damagesDetailData, 
  damagesDetailLoading, 
  selectedYear, 
  setSelectedYear, 
  selectedPeriod, 
  setSelectedPeriod, 
  onClose 
}) => {
  const formatCurrency = formatFullCurrency;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col border border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between p-5 bg-red-950/50 border-b border-red-900/50">
          <div>
            <h2 className="text-xl font-bold text-white font-['Manrope']">
              Damages Detail
            </h2>
            {damagesDetailData && (
              <p className="text-sm text-red-400 mt-1">
                {damagesDetailData.payrule} - {damagesDetailData.state_name}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {damagesDetailLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
            </div>
          ) : damagesDetailData && damagesDetailData.is_applicable ? (
            <div className="space-y-6">
              {/* Violation Type and Formula */}
              <div className="p-4 rounded-xl bg-slate-800 border border-slate-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-red-900/50">
                    <ShieldAlert className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">
                      {getViolationTypeLabel(damagesDetailData.violation_type)} Violation
                    </p>
                    <p className="text-xs text-slate-400">
                      {damagesDetailData.hour_type} hours not properly compensated
                    </p>
                  </div>
                </div>
                {/* Formula Box */}
                <div className="p-3 rounded-lg bg-slate-900 border border-slate-600">
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Damages Formula</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-1 rounded bg-blue-900/50 text-blue-300 text-sm font-mono">Hourly Rate</span>
                    <span className="text-slate-500">x</span>
                    <span className="px-2 py-1 rounded bg-red-900/50 text-red-300 text-sm font-mono">{damagesDetailData.damages_config.wage_multiplier}</span>
                    <span className="text-slate-500">x</span>
                    <span className="px-2 py-1 rounded bg-amber-900/50 text-amber-300 text-sm font-mono">{damagesDetailData.hour_type} Hrs</span>
                    <span className="text-slate-500">=</span>
                    <span className="px-2 py-1 rounded bg-red-600 text-white text-sm font-semibold">Back Wages</span>
                  </div>
                  {damagesDetailData.damages_config.liquidated_multiplier > 0 && (
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                      <span className="px-2 py-1 rounded bg-red-600 text-white text-sm font-semibold">Back Wages</span>
                      <span className="text-slate-500">x</span>
                      <span className="px-2 py-1 rounded bg-purple-900/50 text-purple-300 text-sm font-mono">{damagesDetailData.damages_config.liquidated_multiplier}</span>
                      <span className="text-slate-500">=</span>
                      <span className="px-2 py-1 rounded bg-purple-600 text-white text-sm font-semibold">
                        Liquidated Damages
                        <InfoTooltip text="Additional damages equal to a multiplier of back wages, awarded when an employer's violation is deemed willful. Under FLSA, this can double the back wages owed." />
                      </span>
                    </div>
                  )}
                  {damagesDetailData.damages_config.civil_penalty_max_per_violation > 0 && (
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                      <span className="px-2 py-1 rounded bg-orange-900/50 text-orange-300 text-sm font-mono">Violations</span>
                      <span className="text-slate-500">x</span>
                      <span className="px-2 py-1 rounded bg-orange-900/50 text-orange-300 text-sm font-mono">
                        ${damagesDetailData.damages_config.civil_penalty_min_per_violation} - ${damagesDetailData.damages_config.civil_penalty_max_per_violation}
                      </span>
                      <span className="text-slate-500">=</span>
                      <span className="px-2 py-1 rounded bg-orange-600 text-white text-sm font-semibold">
                        Civil Penalties
                        <InfoTooltip text="Fines imposed by state labor agencies (e.g., PAGA in California) on top of back wages. These are paid to the state and/or employees as a deterrent against wage violations." />
                      </span>
                    </div>
                  )}
                  <p className="text-xs text-slate-500 mt-3">
                    {damagesDetailData.damages_config.formula_description}
                  </p>
                </div>
              </div>

              {/* State Statute Details - Collapsible */}
              {(damagesDetailData.damages_config.statute_reference || damagesDetailData.damages_config.statute_summary) && (
                <StatuteDetailsSection 
                  config={damagesDetailData.damages_config}
                  stateName={damagesDetailData.state_name}
                  violationType={getViolationTypeLabel(damagesDetailData.violation_type)}
                />
              )}

              {/* Year Comparison */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* 2025 */}
                <div className="p-4 rounded-xl bg-slate-800 border border-slate-700">
                  <h4 className="text-sm font-semibold text-slate-400 mb-3">2025 Potential Exposure</h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-3xl font-bold text-red-400">
                        {damagesDetailData.summary['2025'].total_damages_max > damagesDetailData.summary['2025'].total_damages_min
                          ? `${formatCurrency(damagesDetailData.summary['2025'].total_damages_min)} - ${formatCurrency(damagesDetailData.summary['2025'].total_damages_max)}`
                          : formatCurrency(damagesDetailData.summary['2025'].total_damages_min)
                        }
                      </p>
                      <span className="text-xs text-slate-500">Total potential damages</span>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Back Wages:</span>
                        <span className="text-red-300">{formatCurrency(damagesDetailData.summary['2025'].back_wages)}</span>
                      </div>
                      {damagesDetailData.summary['2025'].liquidated_damages > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-400">
                            Liquidated Damages:
                            <InfoTooltip text="Additional damages equal to a multiplier of back wages, awarded when an employer's violation is deemed willful." />
                          </span>
                          <span className="text-purple-300">{formatCurrency(damagesDetailData.summary['2025'].liquidated_damages)}</span>
                        </div>
                      )}
                      {damagesDetailData.summary['2025'].civil_penalty_max > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-400">
                            Civil Penalties:
                            <InfoTooltip text="Fines imposed by state labor agencies on top of back wages. Range depends on violation severity." />
                          </span>
                          <span className="text-orange-300">
                            {formatCurrency(damagesDetailData.summary['2025'].civil_penalty_min)} - {formatCurrency(damagesDetailData.summary['2025'].civil_penalty_max)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-700">
                      <div>
                        <p className="text-lg font-semibold text-white">
                          {formatCurrency(damagesDetailData.summary['2025'].monthly_max > damagesDetailData.summary['2025'].monthly_min 
                            ? damagesDetailData.summary['2025'].monthly_max 
                            : damagesDetailData.summary['2025'].monthly_min)}
                        </p>
                        <p className="text-xs text-slate-500">Monthly (max)</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-white">
                          {damagesDetailData.summary['2025'].total_hours.toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-500">Hours</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-white">
                          {damagesDetailData.summary['2025'].employee_count}
                        </p>
                        <p className="text-xs text-slate-500">Employees</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2026 */}
                <div className="p-4 rounded-xl bg-slate-800 border border-red-700/50">
                  <h4 className="text-sm font-semibold text-red-400 mb-3">2026 Projected Exposure</h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-3xl font-bold text-red-400">
                        {damagesDetailData.summary['2026'].total_damages_max > damagesDetailData.summary['2026'].total_damages_min
                          ? `${formatCurrency(damagesDetailData.summary['2026'].total_damages_min)} - ${formatCurrency(damagesDetailData.summary['2026'].total_damages_max)}`
                          : formatCurrency(damagesDetailData.summary['2026'].total_damages_min)
                        }
                      </p>
                      <span className="text-xs text-slate-500">If not addressed</span>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Back Wages:</span>
                        <span className="text-red-300">{formatCurrency(damagesDetailData.summary['2026'].back_wages)}</span>
                      </div>
                      {damagesDetailData.summary['2026'].liquidated_damages > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-400">
                            Liquidated Damages:
                            <InfoTooltip text="Additional damages equal to a multiplier of back wages, awarded when an employer's violation is deemed willful." />
                          </span>
                          <span className="text-purple-300">{formatCurrency(damagesDetailData.summary['2026'].liquidated_damages)}</span>
                        </div>
                      )}
                      {damagesDetailData.summary['2026'].civil_penalty_max > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-400">
                            Civil Penalties:
                            <InfoTooltip text="Fines imposed by state labor agencies on top of back wages. Range depends on violation severity." />
                          </span>
                          <span className="text-orange-300">
                            {formatCurrency(damagesDetailData.summary['2026'].civil_penalty_min)} - {formatCurrency(damagesDetailData.summary['2026'].civil_penalty_max)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-700">
                      <div>
                        <p className="text-lg font-semibold text-white">
                          {formatCurrency(damagesDetailData.summary['2026'].monthly_max > damagesDetailData.summary['2026'].monthly_min 
                            ? damagesDetailData.summary['2026'].monthly_max 
                            : damagesDetailData.summary['2026'].monthly_min)}
                        </p>
                        <p className="text-xs text-slate-500">Monthly (max)</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-white">
                          {damagesDetailData.summary['2026'].total_hours.toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-500">Hours</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-white">
                          {damagesDetailData.summary['2026'].employee_count}
                        </p>
                        <p className="text-xs text-slate-500">Employees</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Non-Compliance Reason */}
              {damagesDetailData.compliance_reason && (
                <div className="p-4 rounded-lg bg-red-950/30 border border-red-800/50">
                  <p className="text-xs font-semibold text-red-400 uppercase tracking-wide mb-2">Why Non-Compliant</p>
                  <p className="text-sm text-slate-300">{damagesDetailData.compliance_reason}</p>
                </div>
              )}

              {/* Employee Breakdown Table */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-white">Employee Damages Breakdown ({selectedYear} - {selectedPeriod})</h4>
                  <div className="flex gap-2">
                    <div className="flex gap-1">
                      <button
                        onClick={() => setSelectedYear('2025')}
                        className={`px-2 py-1 text-xs rounded-md font-medium transition-colors ${
                          selectedYear === '2025'
                            ? 'bg-slate-600 text-white'
                            : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                        }`}
                      >
                        2025
                      </button>
                      <button
                        onClick={() => setSelectedYear('2026')}
                        className={`px-2 py-1 text-xs rounded-md font-medium transition-colors ${
                          selectedYear === '2026'
                            ? 'bg-red-600 text-white'
                            : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                        }`}
                      >
                        2026
                      </button>
                    </div>
                    <div className="flex gap-1">
                      {['monthly', 'quarterly', 'annual'].map((period) => (
                        <button
                          key={period}
                          onClick={() => setSelectedPeriod(period)}
                          className={`px-2 py-1 text-xs rounded-md font-medium transition-colors ${
                            selectedPeriod === period
                              ? 'bg-red-600 text-white'
                              : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                          }`}
                        >
                          {period.charAt(0).toUpperCase() + period.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-700 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-800">
                        <th className="text-left p-3 text-slate-400 font-medium">Employee ID</th>
                        <th className="text-left p-3 text-slate-400 font-medium">State</th>
                        <th className="text-right p-3 text-slate-400 font-medium">Hourly Rate</th>
                        <th className="text-right p-3 text-slate-400 font-medium">{damagesDetailData.hour_type} Hrs</th>
                        <th className="text-right p-3 text-slate-400 font-medium">Back Wages</th>
                        {damagesDetailData.damages_config.liquidated_multiplier > 0 && (
                          <th className="text-right p-3 text-slate-400 font-medium">
                            Liquidated
                            <InfoTooltip text="Additional damages equal to back wages x liquidated multiplier" />
                          </th>
                        )}
                        {damagesDetailData.damages_config.civil_penalty_max_per_violation > 0 && (
                          <th className="text-right p-3 text-slate-400 font-medium">
                            Civil (max)
                            <InfoTooltip text="Maximum civil penalty based on violations" />
                          </th>
                        )}
                        <th className="text-right p-3 text-slate-400 font-medium">Total (max)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedYear === '2025' ? damagesDetailData.employees_2025 : damagesDetailData.employees_2026).slice(0, 10).map((emp, idx) => {
                        const periodDivisor = selectedPeriod === 'monthly' ? 12 : selectedPeriod === 'quarterly' ? 4 : 1;
                        const backWages = emp.back_wages / periodDivisor;
                        const liquidated = emp.liquidated_damages / periodDivisor;
                        const civilMax = emp.civil_penalty_max / periodDivisor;
                        const total = backWages + liquidated + civilMax;
                        
                        return (
                          <tr key={emp.employee_id} className={`border-t border-slate-700 ${idx % 2 === 0 ? 'bg-slate-900/50' : ''}`}>
                            <td className="p-3 text-white font-mono text-xs">{emp.employee_id}</td>
                            <td className="p-3 text-slate-300">{emp.state}</td>
                            <td className="p-3 text-right text-blue-300 font-mono">${emp.hourly_rate.toFixed(2)}</td>
                            <td className="p-3 text-right text-amber-300 font-mono">
                              {(emp.hours / periodDivisor).toFixed(1)}
                            </td>
                            <td className="p-3 text-right text-red-300 font-mono">
                              {formatCurrency(backWages)}
                            </td>
                            {damagesDetailData.damages_config.liquidated_multiplier > 0 && (
                              <td className="p-3 text-right text-purple-300 font-mono">
                                {formatCurrency(liquidated)}
                              </td>
                            )}
                            {damagesDetailData.damages_config.civil_penalty_max_per_violation > 0 && (
                              <td className="p-3 text-right text-orange-300 font-mono">
                                {formatCurrency(civilMax)}
                              </td>
                            )}
                            <td className="p-3 text-right text-red-400 font-semibold">
                              {formatCurrency(total)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-800 border-t-2 border-red-700">
                        <td colSpan={damagesDetailData.damages_config.liquidated_multiplier > 0 && damagesDetailData.damages_config.civil_penalty_max_per_violation > 0 ? 7 : 
                                     damagesDetailData.damages_config.liquidated_multiplier > 0 || damagesDetailData.damages_config.civil_penalty_max_per_violation > 0 ? 6 : 5} 
                            className="p-3 text-right text-sm font-semibold text-white">
                          Total Potential Damages:
                        </td>
                        <td className="p-3 text-right text-red-400 font-bold text-lg">
                          {(() => {
                            const yearData = damagesDetailData.summary[selectedYear];
                            const periodDivisor = selectedPeriod === 'monthly' ? 12 : selectedPeriod === 'quarterly' ? 4 : 1;
                            const total = yearData.total_damages_max / periodDivisor;
                            return formatCurrency(total);
                          })()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                  {(selectedYear === '2025' ? damagesDetailData.employees_2025 : damagesDetailData.employees_2026).length > 10 && (
                    <div className="p-3 bg-slate-800 text-center text-xs text-slate-400">
                      Showing top 10 of {(selectedYear === '2025' ? damagesDetailData.employees_2025 : damagesDetailData.employees_2026).length} employees
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : damagesDetailData && !damagesDetailData.is_applicable ? (
            <div className="text-center py-8">
              <p className="text-slate-400">{damagesDetailData.message || "This violation type is not applicable in this state."}</p>
            </div>
          ) : (
            <p className="text-slate-400 text-center">Failed to load damages detail</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DamagesDetailModal;
